import React, { useState, useEffect } from 'react';
import { formatAmount } from '../../utils/formatters';

const AmountDistributionTable = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showGlobal, setShowGlobal] = useState(false);
  const [filteredStats, setFilteredStats] = useState({
    minAmount: 0,
    maxAmount: 0,
    avgAmount: 0
  });
  
  // Mettre à jour les statistiques filtrées lorsque les sélections changent
  useEffect(() => {
    // Fonction pour recalculer les statistiques en fonction des filtres
    const recalculateStats = () => {
      if (!data || !data.byYearMonth) return;
      
      // Si aucun filtre n'est appliqué, utiliser les stats globales
      if (!selectedYear && !selectedMonth) {
        return setFilteredStats(data.globalStats);
      }
      
      let relevantData = [];
      
      if (selectedYear && selectedMonth && data.byYearMonth[selectedYear]?.[selectedMonth]) {
        // Données pour l'année et le mois sélectionnés
        relevantData = data.byYearMonth[selectedYear][selectedMonth].tranches;
      } else if (selectedYear && data.byYearMonth[selectedYear]) {
        // Collecter toutes les données pour l'année sélectionnée
        Object.values(data.byYearMonth[selectedYear]).forEach(monthData => {
          relevantData = [...relevantData, ...monthData.tranches];
        });
      } else {
        // Aucune donnée correspondant aux filtres
        setFilteredStats({
          minAmount: 0,
          maxAmount: 0,
          avgAmount: 0
        });
        return;
      }
      
      // Si pas de données pertinentes
      if (relevantData.length === 0) {
        setFilteredStats({
          minAmount: 0,
          maxAmount: 0,
          avgAmount: 0
        });
        return;
      }
      
      // Calculer les nouvelles statistiques
      const amounts = relevantData.map(tranche => tranche.avgAmount);
      const min = Math.min(...amounts);
      const max = Math.max(...amounts);
      
      // Calculer la moyenne pondérée
      let totalAmount = 0;
      let totalCount = 0;
      
      relevantData.forEach(tranche => {
        totalAmount += tranche.totalAmount;
        totalCount += tranche.count;
      });
      
      const avg = totalCount > 0 ? totalAmount / totalCount : 0;
      
      setFilteredStats({
        minAmount: min,
        maxAmount: max,
        avgAmount: avg
      });
    };
    
    recalculateStats();
  }, [data, selectedYear, selectedMonth]);
  
  // Si pas de données, afficher un message
  if (!data || !data.byYearMonth) {
    return (
      <div className="bg-white shadow rounded-lg p-5 mb-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800">
          Répartition des montants décaissés
        </h2>
        <div className="p-8 text-center text-gray-500">
          Aucune donnée disponible pour la répartition des montants.
        </div>
      </div>
    );
  }
  
  const { byYearMonth } = data;
  
  // Extraire la liste des années disponibles
  const availableYears = Object.keys(byYearMonth).sort((a, b) => b - a); // Tri descendant
  
  // Extraire la liste des mois disponibles pour l'année sélectionnée
  const availableMonths = selectedYear 
    ? Object.keys(byYearMonth[selectedYear]).map(month => ({
        value: month,
        label: byYearMonth[selectedYear][month].monthName
      })).sort((a, b) => a.value - b.value) // Tri ascendant par numéro de mois
    : [];
  
  // Déterminer les données à afficher
  let dataToDisplay = [];
  if (selectedYear && selectedMonth && byYearMonth[selectedYear][selectedMonth]) {
    // Afficher les données pour l'année et le mois sélectionnés
    dataToDisplay = byYearMonth[selectedYear][selectedMonth].tranches;
  } else if (selectedYear) {
    // Calculer la moyenne pour tous les mois de l'année sélectionnée
    const trancheMap = new Map();
    
    Object.values(byYearMonth[selectedYear]).forEach(monthData => {
      monthData.tranches.forEach(tranche => {
        if (!trancheMap.has(tranche.tranche)) {
          trancheMap.set(tranche.tranche, {
            tranche: tranche.tranche,
            trancheLabel: tranche.trancheLabel,
            count: 0,
            totalAmount: 0,
            contracts: 0
          });
        }
        
        const existing = trancheMap.get(tranche.tranche);
        existing.count += tranche.count;
        existing.totalAmount += tranche.totalAmount;
        existing.contracts += tranche.count;
      });
    });
    
    dataToDisplay = Array.from(trancheMap.values())
      .map(item => ({
        ...item,
        avgAmount: item.totalAmount / item.count
      }))
      .sort((a, b) => a.tranche - b.tranche);
  } else {
    // Calculer la moyenne pour toutes les années
    const trancheMap = new Map();
    
    Object.values(byYearMonth).forEach(yearData => {
      Object.values(yearData).forEach(monthData => {
        monthData.tranches.forEach(tranche => {
          if (!trancheMap.has(tranche.tranche)) {
            trancheMap.set(tranche.tranche, {
              tranche: tranche.tranche,
              trancheLabel: tranche.trancheLabel,
              count: 0,
              totalAmount: 0,
              contracts: 0
            });
          }
          
          const existing = trancheMap.get(tranche.tranche);
          existing.count += tranche.count;
          existing.totalAmount += tranche.totalAmount;
          existing.contracts += tranche.count;
        });
      });
    });
    
    dataToDisplay = Array.from(trancheMap.values())
      .map(item => ({
        ...item,
        avgAmount: item.totalAmount / item.count
      }))
      .sort((a, b) => a.tranche - b.tranche);
  }
  
  // Calculer le total
  const totalContracts = dataToDisplay.reduce((sum, item) => sum + item.count, 0);
  const totalAmount = dataToDisplay.reduce((sum, item) => sum + item.totalAmount, 0);
  const overallAvg = totalContracts > 0 ? totalAmount / totalContracts : 0;
  
  return (
    <div className="bg-white shadow rounded-lg p-5 mb-6 hover:shadow-lg transition-shadow">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 md:mb-0">
          Répartition des montants décaissés par tranches de 50 000 FCFA
        </h2>
        
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 w-full md:w-auto">
          {/* Sélecteur d'année */}
          <div className="flex flex-col w-full sm:w-auto">
            <label htmlFor="year-select" className="font-medium text-gray-700 mb-1 text-sm">
              Année:
            </label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setSelectedMonth(''); // Réinitialiser le mois lors du changement d'année
              }}
              className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
            >
              <option value="">Toutes les années</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          {/* Sélecteur de mois (visible uniquement si une année est sélectionnée) */}
          {selectedYear && (
            <div className="flex flex-col w-full sm:w-auto">
              <label htmlFor="month-select" className="font-medium text-gray-700 mb-1 text-sm">
                Mois:
              </label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="form-select rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 transition-colors"
              >
                <option value="">Tous les mois</option>
                {availableMonths.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
      
      {/* Statistiques globales - maintenant avec les données filtrées */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-indigo-50 p-3 rounded-lg">
          <p className="text-xs text-indigo-600">Montant minimum</p>
          <p className="text-lg font-bold text-indigo-800">{formatAmount(filteredStats.minAmount)}</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-green-600">Montant moyen</p>
          <p className="text-lg font-bold text-green-800">{formatAmount(filteredStats.avgAmount)}</p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <p className="text-xs text-purple-600">Montant maximum</p>
          <p className="text-lg font-bold text-purple-800">{formatAmount(filteredStats.maxAmount)}</p>
        </div>
      </div>
      
      {/* Titre dynamique avec bouton pour déplier/replier */}
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-md font-medium text-gray-700">
          {selectedYear && selectedMonth 
            ? `Répartition pour ${byYearMonth[selectedYear][selectedMonth].monthName} ${selectedYear}`
            : selectedYear 
              ? `Répartition pour l'année ${selectedYear}`
              : 'Répartition globale'
          }
        </h3>
        
        {/* Bouton pour déplier/replier */}
        <button 
          onClick={() => setShowGlobal(!showGlobal)}
          className="text-gray-500 hover:text-indigo-600 focus:outline-none transition-colors"
        >
          {showGlobal ? (
            <span className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              Replier
            </span>
          ) : (
            <span className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Déplier
            </span>
          )}
        </button>
      </div>
      
      {/* Tableau de répartition - maintenant conditionnel */}
      {showGlobal && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tranche (FCFA)
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre de contrats
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant total
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant moyen
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pourcentage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dataToDisplay.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatAmount(item.tranche)} - {formatAmount(item.tranche + 49999)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatAmount(item.totalAmount)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatAmount(item.avgAmount)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {((item.count / totalContracts) * 100).toFixed(1)}%
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full" 
                        style={{ width: `${(item.count / totalContracts) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
              
              {/* Ligne de totaux */}
              <tr className="bg-gray-100 font-medium">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">TOTAL</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">{totalContracts}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">{formatAmount(totalAmount)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">{formatAmount(overallAvg)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">100%</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AmountDistributionTable;